/* global axios */
import ApiClient from './ApiClient';

/**
 * Smarticks-specific endpoints, all under /api/v1/accounts/:id/smarticks/*.
 *
 * `accountScoped: true` makes ApiClient build that prefix and, importantly, use `window.axios` —
 * which carries the auth headers. A bare `import axios` does not, and every such call 401s.
 */
class SmarticksAPI extends ApiClient {
  constructor() {
    super('smarticks', { accountScoped: true });
  }

  getVoiceQueues() {
    return axios.get(`${this.url}/voice_queues`);
  }

  // The server answers with the freshly saved matrix AND re-discovered queues, so the screen never
  // has to guess what it just wrote — and an enabled_at stamped server-side is the one that counts.
  updateVoiceQueues(selection) {
    return axios.put(`${this.url}/voice_queues`, { queues: selection });
  }

  // ── Dr. Clinix (DrWeb) — the conversation-sidebar panel ──────────────────────────────────
  // -> Api::V1::Accounts::Smarticks::DrwebController. Every answer is HTTP 200 with
  // `{ ok, code, message }` on refusal (the Hebrew message is the server's, W6); only Pundit
  // (401) and a foreign conversation (404) come back as thrown axios errors.
  drwebStatus() {
    return axios.get(`${this.url}/drweb/status`);
  }

  drwebConversation(displayId) {
    return axios.get(`${this.url}/drweb/conversations/${displayId}`);
  }

  // v1.1: `appointment_id` is what the panel saw at that ordinal — the server refuses a shifted
  // list as `list_changed` instead of writing on whatever now sits at that index.
  drwebRemark(displayId, { index, appointment_id, text }) {
    return axios.post(`${this.url}/drweb/conversations/${displayId}/remark`, {
      index,
      appointment_id,
      text,
    });
  }

  drwebTicket(displayId, { details }) {
    return axios.post(`${this.url}/drweb/conversations/${displayId}/ticket`, {
      details,
    });
  }

  // v1.1 WP-A — book + cancel from the panel (any agent who may see the conversation)
  drwebFreeTime(displayId, { scheduler_id, branch_id, date }) {
    return axios.post(
      `${this.url}/drweb/conversations/${displayId}/free_time`,
      { scheduler_id, branch_id, date }
    );
  }

  drwebBook(displayId, { scheduler_id, branch_id, at }) {
    return axios.post(`${this.url}/drweb/conversations/${displayId}/book`, {
      scheduler_id,
      branch_id,
      at,
    });
  }

  drwebCancel(displayId, { index, appointment_id, reason }) {
    return axios.post(`${this.url}/drweb/conversations/${displayId}/cancel`, {
      index,
      appointment_id,
      reason,
    });
  }

  drwebPatient(displayId, { customerId }) {
    return axios.post(`${this.url}/drweb/conversations/${displayId}/patient`, {
      customer_id: customerId,
    });
  }
}

export default new SmarticksAPI();
